import Vue from 'vue'
import { i18n } from '~/plugins/i18n'

// locale 到 iso 的映射
const localeToIsoMap = {
  en: 'en-US',
  zh: 'zh-CN',
}

// 获取当前 locale 对应的 iso
export function getCurrentIso() {
  const currentLocale = i18n.locale || 'zh'
  return localeToIsoMap[currentLocale] || localeToIsoMap['zh']
}

export const getUrlParam = (url) => {
  const obj = {}
  const arr = url.split('?')
  if (arr.length > 1) {
    const params = arr[1].split('&')
    params.map((v) => (obj[v.split('=')[0]] = v.split('=')[1]))
  }
  return obj
}

/**
 * 复制文本到剪贴板。优先使用 Clipboard API，避免对可编辑控件取焦点导致移动端弹出键盘。
 * @param {string|number|null|undefined} text
 * @returns {Promise<{ ok: boolean, error?: 'empty' | 'exec_failed' | 'exception' | 'no_document' }>}
 */
export async function copyText(text) {
  if (text == null || text === '') {
    return { ok: false, error: 'empty' }
  }
  const str = String(text)

  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(str)
      return { ok: true }
    } catch (e) {
      // 降级到 execCommand
    }
  }

  if (typeof document === 'undefined') {
    return { ok: false, error: 'no_document' }
  }

  const ta = document.createElement('textarea')
  ta.value = str
  ta.readOnly = true
  ta.setAttribute('readonly', 'readonly')
  ta.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:none;opacity:0;pointer-events:none;'
  document.body.appendChild(ta)
  try {
    ta.select()
    ta.setSelectionRange(0, str.length)
    const copied = document.execCommand('copy')
    return copied ? { ok: true } : { ok: false, error: 'exec_failed' }
  } catch (err) {
    return { ok: false, error: 'exception' }
  } finally {
    document.body.removeChild(ta)
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur()
    }
  }
}

export const _sub = (params) => {
  let temp = urlEncode(params)
  return temp && '?' + temp.substring(1)
}

function urlEncode(param, key, encode) {
  if (param == null) return ''
  var paramStr = ''
  var t = typeof param
  if (t == 'string' || t == 'number' || t == 'boolean') {
    paramStr += '&' + key + '=' + (encode == null || encode ? encodeURIComponent(param) : param)
  } else {
    for (var i in param) {
      var k = key == null ? i : key + (param instanceof Array ? '[' + i + ']' : '.' + i)
      paramStr += urlEncode(param[i], k, encode)
    }
  }
  return paramStr
}

/**
 * 字典值替换文本通用方法
 * @param dictOptions  字典数组
 * @param text  字典值
 * @return String
 */
export function filterDictText(dictOptions, text) {
  if (text != null && Array.isArray(dictOptions)) {
    let result = []
    // 允许多个逗号分隔，允许传数组对象
    let splitText
    if (Array.isArray(text)) {
      splitText = text
    } else {
      splitText = text.toString().trim().split(',')
    }
    for (let txt of splitText) {
      let dictText = txt
      for (let dictItem of dictOptions) {
        if (txt.toString() === dictItem.value.toString()) {
          if (dictItem.langContent) {
            dictText = JSON.parse(dictItem.langContent)[getCurrentIso()]
          } else if (dictItem.internationalKey) {
            dictText = i18n.t(dictItem['internationalKey'])
          } else {
            dictText = dictItem.text || dictItem.title || dictItem.label
          }
          break
        }
      }
      result.push(dictText)
    }
    return result.join(',')
  }
  return text
}

/**
 *
 * @desc 将金额转成千分位
 * @params value 需要转换的金额
 * @params isFix 是否需要保留小数
 * @params decimal 需要保留几位小数
 */
export function formatAmount(value, isFix = false, decimal = 2) {
  if (typeof value === 'number') {
    if (isFix) {
      value = Number(value.toFixed(decimal))
      if (value === 0) value = 0
    }
    return value.toLocaleString()
  }
  return 0
}

/**
 * 随机生成数字
 *
 * 示例：生成长度为 12 的随机数：randomNumber(12)
 * 示例：生成 3~23 之间的随机数：randomNumber(3, 23)
 *
 * @param1 最小值 | 长度
 * @param2 最大值
 * @return int 生成后的数字
 */
export function randomNumber() {
  // 生成 最小值 到 最大值 区间的随机数
  const random = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1) + min)
  }
  if (arguments.length === 1) {
    let [length] = arguments
    // 生成指定长度的随机数字，首位一定不是 0
    let nums = [...Array(length).keys()].map((i) => (i > 0 ? random(0, 9) : random(1, 9)))
    return parseInt(nums.join(''))
  } else if (arguments.length >= 2) {
    let [min, max] = arguments
    return random(min, max)
  } else {
    return Number.NaN
  }
}

/**
 * 随机生成字符串
 * @param length 字符串的长度
 * @param chats 可选字符串区间（只会生成传入的字符串中的字符）
 * @return string 生成的字符串
 */
export function randomString(length, chats) {
  if (!length) length = 1
  if (!chats) chats = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let str = ''
  for (let i = 0; i < length; i++) {
    let num = randomNumber(0, chats.length - 1)
    str += chats[num]
  }
  return str
}

/**
 * 随机生成uuid
 * @return string 生成的uuid
 */
export function randomUUID() {
  let chats = '0123456789abcdef'
  return randomString(32, chats)
}

export { encrypt, decrypt, encryptWithPublicKey } from './crypto'

/**
 * 深拷贝
 * @param {*} obj 需要深拷贝的对象
 * @param {WeakMap} cache 用于处理循环引用
 * @return {*} 深拷贝后的对象
 */
export function deepClone(obj, cache = new WeakMap()) {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj)
  if (obj instanceof RegExp) return new RegExp(obj)

  if (cache.has(obj)) return cache.get(obj)

  const clone = Array.isArray(obj) ? [] : Object.create(Object.getPrototypeOf(obj))
  cache.set(obj, clone)

  for (const key of Reflect.ownKeys(obj)) {
    clone[key] = deepClone(obj[key], cache)
  }
  return clone
}

/** 跳转错误页 */
export function showPageError(router, err) {
  if (!router || !err) return
  const statusCode = err.code ?? err.statusCode ?? err.response?.status ?? 500
  const message = err.message || ''
  router.replace({
    name: 'error',
    query: {
      statusCode: String(statusCode),
      ...(message ? { message } : {}),
    },
  })
}
